package kz.dentalcrm.app.core.ui.components.refresh

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.Velocity
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlin.math.absoluteValue

@Composable
fun rememberPullToRefreshState(
    isRefreshing: Boolean
): PullToRefreshState = remember {
    PullToRefreshState(isRefreshing)
}.apply { this.isRefreshing = isRefreshing }

@Stable
class PullToRefreshState(
    isRefreshing: Boolean
) {
    private val _contentOffset = Animatable(0f)

    var isRefreshing by mutableStateOf(isRefreshing)

    var isPullInProgress: Boolean by mutableStateOf(false)
        internal set

    val isResting: Boolean get() = _contentOffset.isRunning && !isRefreshing

    val contentOffset: Float get() = _contentOffset.value

    internal suspend fun animateOffsetTo(offset: Float) {
        _contentOffset.animateTo(offset)
    }

    internal suspend fun dispatchScrollDelta(delta: Float) {
        _contentOffset.snapTo(_contentOffset.value + delta)
    }
}

private class PullToRefreshNestedScrollConnection(
    private val state: PullToRefreshState,
    private val coroutineScope: CoroutineScope,
    private val onRefresh: () -> Unit,
) : NestedScrollConnection {
    var enabled: Boolean = false
    var refreshTrigger: Float = 0f
    var dragMultiplier: Float = 0f

    // Debounce job for web (mouse/trackpad never fires onPreFling).
    // onPreFling cancels this immediately on touch — no behaviour change for iOS/Android.
    private var scrollEndJob: Job? = null

    private fun scheduleScrollEndCheck() {
        scrollEndJob?.cancel()
        scrollEndJob = coroutineScope.launch {
            delay(SCROLL_END_TIMEOUT_MS)
            if (!state.isRefreshing && state.contentOffset >= refreshTrigger) {
                onRefresh()
            }
            state.isPullInProgress = false
        }
    }

    override fun onPreScroll(
        available: Offset,
        source: NestedScrollSource
    ): Offset = when {
        !enabled -> Offset.Zero
        state.isRefreshing -> Offset(0f, available.y)
        source == NestedScrollSource.UserInput && available.y < 0 -> dragUp(available)
        else -> Offset.Zero
    }

    @Suppress("MagicNumber")
    private fun dragUp(available: Offset): Offset {
        state.isPullInProgress = true
        val newOffset = (available.y * dragMultiplier + state.contentOffset).coerceAtLeast(0f)
        val dragConsumed = newOffset - state.contentOffset

        return if (dragConsumed.absoluteValue >= 0.5f) {
            coroutineScope.launch { state.dispatchScrollDelta(dragConsumed) }
            scheduleScrollEndCheck()
            Offset(x = 0f, y = available.y)
        } else {
            Offset.Zero
        }
    }

    override fun onPostScroll(
        consumed: Offset,
        available: Offset,
        source: NestedScrollSource
    ): Offset = when {
        !enabled -> Offset.Zero
        state.isRefreshing -> Offset.Zero
        source == NestedScrollSource.UserInput && available.y > 0 -> dragDown(available)
        else -> Offset.Zero
    }

    private fun dragDown(available: Offset): Offset {
        state.isPullInProgress = true
        coroutineScope.launch { state.dispatchScrollDelta(available.y * dragMultiplier) }
        scheduleScrollEndCheck()
        return Offset(x = 0f, y = available.y)
    }

    override suspend fun onPreFling(available: Velocity): Velocity {
        // Touch fling: cancel debounce and handle immediately.
        scrollEndJob?.cancel()

        if (!state.isRefreshing && state.contentOffset >= refreshTrigger) {
            onRefresh()
        }
        state.isPullInProgress = false

        return when {
            state.contentOffset != 0f -> available
            else -> Velocity.Zero
        }
    }

    companion object {
        private const val SCROLL_END_TIMEOUT_MS = 200L
    }
}

@Composable
fun PullToRefresh(
    state: PullToRefreshState,
    onRefresh: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    dragMultiplier: Float = 0.5f,
    refreshTriggerDistance: Dp = 60.dp,
    refreshingOffset: Dp = refreshTriggerDistance,
    indicatorPadding: PaddingValues = PaddingValues(0.dp),
    indicator: @Composable (state: PullToRefreshState,
                            refreshTrigger: Dp,
                            refreshingOffset: Dp) -> Unit = { s, trigger, offset ->
        PullToRefreshIndicator(s, trigger, offset)
    },
    clipIndicatorToPadding: Boolean = true,
    content: @Composable () -> Unit,
) {
    require(dragMultiplier in 0f..1f) { "dragMultiplier must be >= 0 and <= 1" }
    require(refreshingOffset <= refreshTriggerDistance) { "refreshingOffset must be <= refreshTriggerDistance" }

    val coroutineScope = rememberCoroutineScope()
    val updatedOnRefresh by rememberUpdatedState(onRefresh)

    val refreshingOffsetPx = with(LocalDensity.current) { refreshingOffset.toPx() }

    LaunchedEffect(Unit) {
        snapshotFlow { !state.isPullInProgress to state.isRefreshing }
            .distinctUntilChanged()
            .filter { it.first }
            .map { it.second }
            .collectLatest { isRefreshing ->
                state.animateOffsetTo(if (isRefreshing) refreshingOffsetPx else 0f)
            }
    }

    val nestedScrollConnection = remember(state, coroutineScope) {
        PullToRefreshNestedScrollConnection(state, coroutineScope) { updatedOnRefresh() }
    }.apply {
        this.enabled = enabled
        this.dragMultiplier = dragMultiplier
        this.refreshTrigger = with(LocalDensity.current) { refreshTriggerDistance.toPx() }
    }

    Box(modifier.nestedScroll(connection = nestedScrollConnection)) {
        Box(modifier = Modifier
            .offset {
                IntOffset(0, state.contentOffset.toInt())
            }
        ) {
            content()
        }
        Box(
            Modifier
                .let { if (!clipIndicatorToPadding) it.clipToBounds() else it }
                .padding(indicatorPadding)
                .matchParentSize()
                .let { if (clipIndicatorToPadding) it.clipToBounds() else it }
        ) {
            Box(Modifier.align(Alignment.TopCenter)) {
                indicator(state, refreshTriggerDistance, refreshingOffset)
            }
        }
    }
}
